import type { IFencingLocation } from '@/Interfaces/Location.interface';
import { fetchGeoData } from '@/components/Map/helpers';
import * as Sentry from '@sentry/react';
import debounce from 'debounce';
import { useCallback, useEffect, useState } from 'react';

interface UseGeoFencingOptions {
  initialGeoFence?: IFencingLocation | null;
  onGeoFenceChange?: (geoFence: IFencingLocation | null) => void;
}

export const useGeoFencing = ({ initialGeoFence, onGeoFenceChange }: UseGeoFencingOptions = {}) => {
  const [address, setAddress] = useState(initialGeoFence?.address || '');
  const [radius, setRadius] = useState<string | number>(initialGeoFence?.radius ?? '');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(
    initialGeoFence ? { latitude: initialGeoFence.latitude, longitude: initialGeoFence.longitude } : null,
  );
  const [selectedAddress, setSelectedAddress] = useState(initialGeoFence?.address || '');

  const handleClear = useCallback(() => {
    setRadius('');
    setAddress('');
    setSelectedAddress('');
    setCoordinates(null);
    setSuggestions([]);
    onGeoFenceChange?.(null);
  }, [onGeoFenceChange]);

  const handleAddressSelect = useCallback((suggestion: string | null) => {
    if (suggestion) {
      setSelectedAddress(suggestion);
      setSuggestions([]);
    }
  }, []);

  const handleAddressInput = useCallback((value: string) => {
    setAddress(value);
    if (!value) {
      setSuggestions([]);
    }
  }, []);

  const handleRadiusInput = useCallback((value: string) => {
    if (/^\d*$/.test(value)) {
      const numValue = value ? Number.parseInt(value, 10) : 0;
      setRadius(numValue > 999 ? 999 : numValue ? numValue : '');
    }
  }, []);

  const fetchSuggestions = useCallback(async (newAddress: string) => {
    try {
      const data = await fetchGeoData(newAddress);
      setSuggestions(data.features.map((feature: { place_name: string }) => feature.place_name));
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching suggestions:', error);
    }
  }, []);

  const debouncedFetchSuggestions = useCallback(debounce(fetchSuggestions, 350), [fetchSuggestions]);

  useEffect(() => {
    if (address.length < 2) {
      setSelectedAddress('');
      return;
    }
    debouncedFetchSuggestions(address);
    return () => {
      debouncedFetchSuggestions.clear();
    };
  }, [address, debouncedFetchSuggestions]);

  const updateCoordinates = useCallback(async () => {
    try {
      const data = await fetchGeoData(selectedAddress);
      if (!data.features[0]) return;
      const [longitude, latitude] = data.features[0].center;
      setCoordinates({ latitude, longitude });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching coordinates:', error);
    }
  }, [selectedAddress]);

  useEffect(() => {
    if (!selectedAddress) {
      setCoordinates(null);
      return;
    }
    updateCoordinates();
  }, [selectedAddress, updateCoordinates]);

  useEffect(() => {
    if (coordinates && radius) {
      onGeoFenceChange?.({
        ...coordinates,
        radius: Number(radius),
        address: selectedAddress,
      });
    } else if (coordinates === null && radius === '') {
      onGeoFenceChange?.(null);
    }
  }, [coordinates, radius, selectedAddress, onGeoFenceChange]);

  const reset = useCallback((newGeoFence: IFencingLocation | null) => {
    if (newGeoFence) {
      setAddress(newGeoFence.address || '');
      setRadius(newGeoFence.radius ?? '');
      setSelectedAddress(newGeoFence.address || '');
      setCoordinates({ latitude: newGeoFence.latitude, longitude: newGeoFence.longitude });
    } else {
      setAddress('');
      setRadius('');
      setSelectedAddress('');
      setCoordinates(null);
    }
    setSuggestions([]);
  }, []);

  return {
    address,
    setAddress,
    radius,
    suggestions,
    coordinates,
    selectedAddress,
    handleClear,
    handleAddressSelect,
    handleAddressInput,
    handleRadiusInput,
    reset,
  };
};

export default useGeoFencing;
